<script setup lang="ts">
import { SwiperSlide } from 'swiper/vue'
import Tabs from '~/components/molecules/Tabs/Tabs.vue'
import Carousel from '~/components/molecules/Carousel/Carousel.vue'
import type { LoyaltyAttractionsItem } from '~/composables/useLoyalty/useLoyalty.types'
import type { ButtonProps } from '~/components/atoms/ButtonV2'
import { ButtonV2 } from '~/components/atoms/ButtonV2'

const props = defineProps<{
  title: string
  subTitle: string
  items: LoyaltyAttractionsItem[]
  button: ButtonProps
}>()

const selectedTab = ref(0)

const tabOptions = computed(() => props.items.map((item) => ({ label: item.tabTitle })))

const activeItem = computed(() => props.items[selectedTab.value])
</script>

<template>
  <section class="w-full px-6 md:px-10 py-6 md:py-10 bg-neutral-6">
    <div class="max-w-7xl mx-auto flex flex-col gap-6 md:gap-8">
      <!-- Header -->
      <div class="flex flex-col items-center gap-4 md:gap-8">
        <div class="flex flex-col md:items-center gap-2 w-full items-start">
          <h2
            class="text-start md:text-center text-foundation-verde1 text-2xl md:text-[32px] font-bold font-mplus leading-7 md:leading-10"
          >
            {{ title }}
          </h2>
          <p
            class="text-start md:text-center text-foundation-verde1 text-base font-normal font-mplus leading-6 max-w-2xl"
          >
            {{ subTitle }}
          </p>
        </div>
        <ButtonV2
          v-if="button"
          v-bind="button"
          :appearance="button?.appearance ?? 'white'"
          class="bg-foundation-verde3 text-neutral-6 !typo-p2-upper whitespace-nowrap !px-6"
        />
      </div>

      <!-- Tabs + Content -->
      <div class="flex flex-col gap-4 md:gap-8">
        <!-- Tabs navigation -->
        <Tabs :tabs="tabOptions" v-model="selectedTab" :show-info-title="false" :fill="true" :align="'start'" />
        <div v-if="activeItem" class="flex flex-col lg:flex-row gap-6">
          <!-- Left column (text) -->
          <div class="w-full lg:w-96 shrink-0 flex flex-col gap-8">
            <!-- Title + description -->
            <div class="flex flex-col gap-2">
              <h3 class="text-foundation-verde1 text-xl md:text-2xl font-bold font-mplus leading-6 md:leading-7">
                {{ activeItem.title }}
              </h3>
              <div
                class="cms-content text-foundation-verde1 text-base font-normal font-mplus leading-6"
                v-html="activeItem.description"
              />
            </div>

            <!-- Image carousel — visible only on mobile, sits between description and list -->
            <div class="block lg:hidden">
              <Carousel
                :show-arrow="false"
                :show-dots="false"
                :infinite="true"
                dots-active-color="var(--color-foundation-verde1)"
                dots-color="var(--color-neutral-5)"
                dots-background-color="rgba(249,249,245,0.50)"
                carousel-class="!h-full rounded-3xl overflow-hidden"
                :custom-breakpoints="{
                  0: { slidesPerView: 1, spaceBetween: 0 },
                  1024: { slidesPerView: 1, spaceBetween: 0 }
                }"
              >
                <SwiperSlide v-for="(image, index) in activeItem.images" :key="index" class="h-52!">
                  <NuxtImg
                    :src="image.url"
                    :alt="image.alt"
                    class="w-full h-full object-cover"
                    sizes="sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw"
                    loading="lazy"
                    format="webp"
                  />
                </SwiperSlide>
              </Carousel>
            </div>

            <!-- List -->
            <div
              class="flex flex-col gap-3 md:gap-4 text-foundation-verde1 typo-p2 fidelidade-wisewig"
              v-html="activeItem.content"
            ></div>
          </div>

          <!-- Image carousel — visible only on desktop -->
          <div class="hidden lg:flex flex-1 w-full h-[454px] overflow-hidden max-w-full">
            <Carousel
              :show-arrow="false"
              :show-dots="true"
              :infinite="true"
              dots-active-color="var(--color-foundation-verde1)"
              dots-color="var(--color-neutral-5)"
              dots-background-color="rgba(249,249,245,0.50)"
              class="h-full w-full max-w-full"
              carousel-class="!h-full rounded-3xl overflow-hidden"
              :custom-breakpoints="{
                0: { slidesPerView: 1, spaceBetween: 0 },
                1024: { slidesPerView: 1, spaceBetween: 0 }
              }"
            >
              <SwiperSlide v-for="(image, index) in activeItem.images" :key="index" class="lg:h-full!">
                <NuxtImg
                  :src="image.url"
                  :alt="image.alt"
                  class="w-full h-full object-cover"
                  sizes="md:50vw lg:50vw xl:50vw 2xl:50vw"
                  loading="lazy"
                  format="webp"
                />
              </SwiperSlide>
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
